import { map } from 'rxjs/operators';
import { BehaviorSubject, fromEvent } from 'rxjs';
import { FromEventTarget } from 'rxjs/internal/observable/fromEvent';
import setup from './setup';
import setupQ2Elements from './setup/elements';

import setupActions from './actions';
import setupSources from './sources';

import { IDict } from './types/utility';
import { Maybe } from 'maybe-not';
import SDKMessageBus from './utility/message-bus';
import t, { ITranslationSetup } from './setup/t';
import resizeIframe from './actions/resizeIframe';
import getParameterByName from './utility/getParameterByName';
import { TestingWindow } from 'q2-tecton-common/lib/utility/message-bus';
import deprecate from 'q2-tecton-common/lib/utility/deprecate';
import { IConnectOptions, IPlatformCoreAPI } from './types';

/**
 * Initializes the Tecton environment for a module.  This function should be the first called when initializing an module.
 *
 * Below is an example setup of a module initializing a VueJS environment.
 *
 * ```javascript
 * import { connect } from 'q2-tecton-sdk';
 * import Vue from 'vue';
 *
 * connect().then(({ sources, actions, params$ }: any) => {
 *   let stateParams = params$.getValue();
 *   new Vue({
 *     el: '#app',
 *     //setup Vue environment
 *     stateParams,
 *     tecton: { sources, actions }
 *   });
 *
 * });
 * ```
 * @param options Options for connecting.
 * @returns A Promise, that when resolved, contains the Tecton SDK API available to the module.
 */

export function connect(options?: IConnectOptions): Promise<IPlatformCoreAPI> {
    const teardowns: (() => void)[] = [];
    const legacy = (options && options.legacy) || false;

    if (options && options.testOptions) {
        const params$ = new BehaviorSubject<IDict<string>>(options.testOptions.initialParams || {});
        const teardowns: (() => void)[] = [];
        const locStub =
            options.testOptions.sources?.loc ||
            function(x: string) {
                return x;
            };
        const resizeIframeStub = () => {
            // noop
        };
        const setParamsStub = (newParams: IDict<any>) => {
            params$.next({ ...params$.getValue(), ...newParams });
        };
        const printWindowStub = () => {
            // noop
        };
        const setFetchingStub = (isFetching: boolean) => {
            // noop
        };
        const paramsChangedStub = (callback: (params: IDict<any>) => void) => {
            const sub = params$.pipe(map(x => x)).subscribe(callback);
            const unsub = () => sub.unsubscribe();
            teardowns.push(unsub);
            return unsub;
        };

        return setupQ2Elements(locStub, resizeIframeStub, teardowns).then(() => {
            return {
                sources: {
                    loc: locStub,
                    t: locStub,
                    paramsChanged: paramsChangedStub,
                    ...((options.testOptions && options.testOptions.sources) || {})
                },
                actions: {
                    setParams: setParamsStub,
                    printWindow: printWindowStub,
                    resizeIframe: resizeIframeStub,
                    setFetching: setFetchingStub,
                    ...((options.testOptions && options.testOptions.actions) || {})
                },
                outletContext: options.testOptions && options.testOptions.outletContext,
                teardown: () => {
                    teardowns.forEach(teardown => teardown());
                }
            };
        });
    }

    if (legacy) {
        const messageBus = new SDKMessageBus();
        return t(messageBus, 'legacy', new Set(), true, options && options.baseLanguagePath)
            .then((tSetup: ITranslationSetup) => {
                return setup(messageBus, 'legacy', tSetup.tFn, new Set(), teardowns, () => {
                    // noop
                });
            })
            .then(setupTeardownFn => {
                return {
                    teardown() {
                        teardowns.forEach(fn => fn());
                        setupTeardownFn();
                        messageBus.destroy();
                    }
                };
            });
    }

    // GET MODULE ID FROM QUERY PARAMS
    const moduleId = (window as TestingWindow).isTesting
        ? (options && options.moduleId) || 'tectonRoute.TESTMODULE'
        : getParameterByName('tct-id');

    const outletContext = getParameterByName('tct-ctxid');

    const params$ = new BehaviorSubject<IDict<string>>({});

    const messageBus = new SDKMessageBus();

    const fetchedPlatformCapabilities = messageBus
        .sendRequest<{ capabilities: string[] }>('getPlatformCapabilities', Maybe.nothing<string>())
        .then(response => {
            return new Set(response.value.capabilities);
        });

    messageBus
        .sendRequest<{
            params: IDict<string>;
        }>('getInitialParams', Maybe.just(JSON.stringify({ moduleId })))
        .then(response => {
            response.value.params && params$.next(response.value.params);
        });

    const paramSubscription = messageBus
        .onBroadcast<{ currentParams: IDict<string> }>(`streamParams::${moduleId}`)
        .pipe(
            map(response => {
                return response.value.currentParams;
            })
        )
        .subscribe(params$);

    return fetchedPlatformCapabilities
        .then(platformCapabilities =>
            Promise.all([
                t(
                    messageBus,
                    moduleId,
                    platformCapabilities,
                    false,
                    options && options.baseLanguagePath
                ),
                fetchedPlatformCapabilities
            ])
        )
        .then(([tSetup, platformCapabilities]) => {
            const resizeIframeFn = resizeIframe(moduleId, platformCapabilities, teardowns);
            return setup(
                messageBus,
                moduleId,
                tSetup.tFn,
                platformCapabilities,
                teardowns,
                resizeIframeFn
            ).then(setupTeardownFn => {
                return {
                    setupTeardownFn,
                    tSetup,
                    paramSubscription,
                    platformCapabilities,
                    resizeIframeFn
                };
            });
        })
        .then(
            ({
                setupTeardownFn,
                tSetup,
                paramSubscription,
                platformCapabilities,
                resizeIframeFn
            }) => {
                const actions = setupActions(
                    messageBus,
                    moduleId,
                    teardowns,
                    platformCapabilities,
                    resizeIframeFn
                );
                const sources = setupSources(
                    messageBus,
                    moduleId,
                    tSetup,
                    teardowns,
                    platformCapabilities,
                    params$
                );
                const iframeTeardown = fromEvent(
                    window as FromEventTarget<any>,
                    'unload'
                ).subscribe(() => {
                    teardown();
                });
                const teardown = () => {
                    teardowns.forEach(fn => fn());
                };

                teardowns.push(() => {
                    tSetup.streamLanguageSubscription.unsubscribe();
                    paramSubscription.unsubscribe();
                    setupTeardownFn();
                    iframeTeardown.unsubscribe();
                    messageBus.destroy();
                });

                const result = {
                    sources,
                    actions,
                    outletContext,
                    teardown
                };

                Object.defineProperty(result, 'params$', {
                    get() {
                        deprecate('params$ is deprecated in favor of sources.paramsChanged()', {
                            package: 'q2-tecton-sdk',
                            until: '1.0.0'
                        });
                        return params$;
                    }
                });

                return result;
            }
        );
}
